import { axiosInstance } from './axiosInstance';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ProviderMeta, SocialPage, SocialPagesResult } from '../types/socialTypes';
import { ValidationError } from '../types/authTypes';

export const fetchSocialPages = createAsyncThunk<
    SocialPagesResult,
    string,
    { rejectValue: { [key: string]: string } }
>('social/fetchSocialPages', async (provider, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/social-pages/${provider}`);

        const pages: SocialPage[] = (response.data.data || []).map((item: any) => ({
            id: item.id,
            providerId: item.attributes.provider_id,
            provider: item.attributes.provider,
            name: item.attributes.name,
            picture: item.attributes.picture,
        }));

        const meta: ProviderMeta = {
            id: response.data.meta?.provider?.id,
            label: response.data.meta?.provider?.label,
            driver: response.data.meta?.provider?.driver,
            count: response.data.meta?.count ?? 0,
        };

        return { provider, pages, meta };
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: `Failed to fetch ${provider} pages` });
    }
});

export const createSocialPage = createAsyncThunk<
    SocialPage,
    { provider: string; name: string; description?: string },
    { rejectValue: { [key: string]: string } }
>('social/createSocialPage', async ({ provider, name, description }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/social-pages/${provider}`, {
            data: {
                type: 'social_page',
                attributes: {
                    name,
                    description,
                },
            },
        });

        const item = response.data.data;

        const page: SocialPage = {
            id: item.id,
            providerId: item.attributes.provider_id,
            provider: item.attributes.provider,
            name: item.attributes.name,
            picture: item.attributes.picture,
        };

        return page;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data?.errors) {
            const validationErrors = error.response.data.errors as ValidationError[];
            const fieldErrors: { [key: string]: string } = {};
            validationErrors.forEach((err) => {
                const field = err.source.pointer.split('/').pop() || '';
                if (field) {
                    fieldErrors[field] = err.detail;
                }
            });
            return rejectWithValue(fieldErrors);
        }
        return rejectWithValue({ general: `Failed to create board for ${provider}` });
    }
});
